import { createSlice } from '@reduxjs/toolkit';
import { handleRejected } from '../../helpers';
import {
  backgroundFetchRules,
  clearError,
  clearPendingChanges,
  fetchRules,
  initializeRules,
  reorderRules,
  setFilter,
  setIsLoading,
  setIsUpdating,
  setRules,
  updateRules,
} from '../actions/rulesActions';
import rulesState from '../states/rulesState';

export const rulesSlice = createSlice({
  name: 'rules',
  initialState: rulesState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchRules.pending, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    }));

    builder.addCase(fetchRules.fulfilled, (state, action) => ({
      ...state,
      isLoading: false,
      rules: action.payload.rules,
      perms: action.payload.perms,
      fields: action.payload.fields || state.fields,
      restrictions: action.payload.restrictions || state.restrictions,
      isInitialized: true,
      error: null,
    }));

    builder.addCase(fetchRules.rejected, (state, action) =>
      handleRejected(state, action)
    );

    builder.addCase(updateRules.pending, (state) => ({
      ...state,
      isUpdating: true,
      error: null,
    }));

    builder.addCase(updateRules.fulfilled, (state, _action) => {
      const cleanedPendingChanges: any = {};
      Object.entries(state.pendingChanges).forEach(([section, rules]) => {
        if (Array.isArray(rules)) {
          cleanedPendingChanges[section] = rules.map((rule: any) => {
            if (rule.key && rule.id) {
              const { ...ruleWithoutKey } = rule;
              return ruleWithoutKey;
            }
            return rule;
          });
        }
      });

      return {
        ...state,
        isUpdating: false,
        rules: { ...state.rules, ...cleanedPendingChanges },
        pendingChanges: {},
        error: null,
      };
    });

    builder.addCase(updateRules.rejected, (state, action) =>
      handleRejected(state, action)
    );

    builder.addCase(backgroundFetchRules.pending, (state) => ({
      ...state,
      isUpdating: true,
      error: null,
    }));

    builder.addCase(backgroundFetchRules.fulfilled, (state, action) => ({
      ...state,
      isUpdating: false,
      rules: action.payload.rules,
      perms: action.payload.perms,
      fields: action.payload.fields || state.fields,
      restrictions: action.payload.restrictions || state.restrictions,
      error: null,
    }));

    builder.addCase(backgroundFetchRules.rejected, (state, action) => ({
      ...state,
      isUpdating: false,
      error: (action.payload as any)?.message || 'Failed to refresh rules',
    }));

    builder.addCase(initializeRules, (state, action) => ({
      ...state,
      url: action.payload.url,
      fields: action.payload.fields || state.fields,
      restrictions: action.payload.restrictions || state.restrictions,
    }));

    builder.addCase(setFilter, (state, action) => ({
      ...state,
      filter: { ...state.filter, ...(action.payload || {}) },
    }));

    builder.addCase(setRules, (state, action) => ({
      ...state,
      pendingChanges: { ...state.pendingChanges, ...action.payload },
    }));

    builder.addCase(reorderRules, (state, action) => ({
      ...state,
      pendingChanges: {
        ...state.pendingChanges,
        [action.payload.ruleType]: action.payload.rules,
      },
    }));

    builder.addCase(setIsLoading, (state, action) => ({
      ...state,
      isLoading: action.payload,
    }));

    builder.addCase(setIsUpdating, (state, action) => ({
      ...state,
      isUpdating: action.payload,
    }));

    builder.addCase(clearError, (state) => ({
      ...state,
      error: null,
    }));

    builder.addCase(clearPendingChanges, (state) => ({
      ...state,
      pendingChanges: {},
    }));
  },
});

export default rulesSlice.reducer;
